import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usersApi } from '../services/apiClient';
import { PROTECTED_PERMISSIONS, FULL_ACCESS_ROLES } from '@caro/shared/constants';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user permissions from the API
  const fetchPermissions = async () => {
    try {
      if (!user?.id) {
        // Set default permissions for unauthenticated users
        setPermissions([
          'login'
        ]);
        setLoading(false);
        return;
      }

      const response = await usersApi.getMyPermissions();

      // API can return permissions in two ways:
      // 1. { success: true, permissions: [...] } - flat array (expected from backend)
      // 2. { success: true, user: { permissions: { custom: [...], default: [...] } } } - nested object (actual response)
      let permissionsArray = [];

      if (response.data.success) {
        if (response.data.permissions && Array.isArray(response.data.permissions)) {
          // Case 1: Flat array at root
          permissionsArray = response.data.permissions;
        } else if (response.data.user?.permissions) {
          // Case 2: Nested structure in user object
          const perms = response.data.user.permissions;
          if (typeof perms === 'object' && !Array.isArray(perms)) {
            // Combine default and custom permissions
            permissionsArray = [
              ...(perms.default || []),
              ...(perms.custom || [])
            ];
          } else if (Array.isArray(perms)) {
            // Already a flat array
            permissionsArray = perms;
          }
        }

        setPermissions(permissionsArray);
      } else {
        throw new Error('Invalid permissions response');
      }
      setError(null);
    } catch (err) {
      // FAIL CLOSED. This used to grant a hardcoded fallback set
      // (['login','devices.view','locations.view','tickets.view','tickets.create'])
      // whenever /permissions/me failed — so a network blip, an expired token or a
      // 500 GRANTED permissions instead of withholding them, and did it silently.
      //
      // A permission system whose failure mode is "more access" cannot be reasoned
      // about: the one moment you most need the gate to hold is the moment something
      // is already wrong. An empty set means the guards deny and the user sees the
      // error, which is recoverable; the old behaviour was not detectable at all.
      //
      // Note this is now load-bearing in a way it was not before: route guards read
      // this context (Step 0 of specs/20260824-rights-consistency-audit), so an empty
      // set really does close pages. That is the intent — but it makes `error` worth
      // surfacing rather than swallowing.
      setError('Failed to fetch permissions');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  // GDPR-protected permissions and full-access roles are imported from
  // @caro/shared/constants — single source of truth across portal + API.

  // Check if user has specific permission
  const hasPermission = (resourceOrPermission, action) => {
    if (!user) return false;

    // Handle both formats: "locations.view" or ("locations", "view")
    let permission;

    if (action !== undefined) {
      // Called with two arguments (old format): hasPermission("locations", "view")
      permission = `${resourceOrPermission}.${action}`;
    } else {
      // Called with one argument (new format): hasPermission("locations.view")
      permission = resourceOrPermission;
    }

    // Full-access roles bypass every permission EXCEPT GDPR-protected ones.
    // Protected permissions (e.g. DPO approval) must always be explicitly granted.
    if (FULL_ACCESS_ROLES.includes(user.role) && !PROTECTED_PERMISSIONS.includes(permission)) {
      return true;
    }

    // Check if permission exists in the flat permissions array
    if (Array.isArray(permissions)) {
      return permissions.includes(permission);
    }

    return false;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionChecks) => {
    // Support both formats: array of strings or array of tuples
    if (Array.isArray(permissionChecks) && permissionChecks.length > 0) {
      if (Array.isArray(permissionChecks[0])) {
        // Array of tuples: [["resource", "action"], ...]
        return permissionChecks.some(([resource, action]) => hasPermission(resource, action));
      } else {
        // Array of strings: ["permission1", "permission2", ...]
        return permissionChecks.some(perm => hasPermission(perm));
      }
    }
    return false;
  };

  // Check if user has all specified permissions
  const hasAllPermissions = (permissionChecks) => {
    // Support both formats: array of strings or array of tuples
    if (Array.isArray(permissionChecks) && permissionChecks.length > 0) {
      if (Array.isArray(permissionChecks[0])) {
        // Array of tuples: [["resource", "action"], ...]
        return permissionChecks.every(([resource, action]) => hasPermission(resource, action));
      } else {
        // Array of strings: ["permission1", "permission2", ...]
        return permissionChecks.every(perm => hasPermission(perm));
      }
    }
    return false;
  };

  // Refresh permissions
  const refreshPermissions = () => {
    fetchPermissions();
  };

  // Initialize permissions on mount or when user changes
  useEffect(() => {
    fetchPermissions();
  }, [user?.id]);

  // Add backward compatibility properties and helper functions
  const canManageDevices = hasPermission('devices.edit') || hasPermission('devices.delete');
  const canManageLocations = hasPermission('locations.edit') || hasPermission('locations.delete');
  // ── THE THREE USER-MANAGEMENT RIGHTS ARE NOT A TIER, THEY ARE A SCOPE ──────
  //
  // `canManageUsers` deliberately keeps its old meaning -- the PERMISSION MATRIX and
  // impersonation -- so no existing call site changes behaviour. The two below are new
  // (spec 20260826-company-user-management LD-3):
  //
  //   canViewUsers      users.view              see the roster of your own company
  //   canManageAccounts users.manage            create + invite, deactivate, role, PIN/badge
  //   canManageUsers    users.permissions.manage edit anyone's rights, impersonate
  //
  // Holding the broader right implies the narrower one. Without that, an administrator
  // holding only users.permissions.manage would see the matrix but no roster to apply it
  // to -- which is how the page behaved before it was split.
  const canManageUsers = hasPermission('users.permissions.manage');
  const canManageAccounts = hasPermission('users.manage') || canManageUsers;
  const canViewUsers = hasPermission('users.view') || canManageAccounts;

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
    canManageDevices, // For backward compatibility
    canManageLocations,
    canManageUsers,
    canManageAccounts,
    canViewUsers,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;